/**
 * The dashboard's Dutch (Flemish) text: the source every other language is
 * typed against (`Messages` in `./types`).
 *
 * Plain and polite, with "je" and "jij", in De Ceuster's own words: kranen,
 * werven, Odoo; "connector" stays "connector". Counts take i18next's plural
 * keys (`_one`, `_other`) and `{{count, number}}`, which writes the number the
 * Belgian way (`formatNumber` in `@/lib/format`).
 */

import type { ApiErrorCode } from "@/lib/api-errors";

export const nl = {
  common: {
    loading: "Laden",
    loadingEllipsis: "Laden…",
    tryAgain: "Opnieuw proberen",
    cancel: "Annuleren",
    close: "Sluiten",
    save: "Opslaan",
    delete: "Verwijderen",
    edit: "Bewerken",
    actionsFor: "Acties voor {{name}}",
    on: "Aan",
    off: "Uit",
    never: "Nooit",
    pageRange: "{{from, number}}–{{to, number}} van {{total, number}}",
    resetFilters: "Filters wissen",
  },
  toast: {
    region: "Berichten",
  },
  language: {
    label: "Taal",
  },
  nav: {
    main: "Hoofdmenu",
    connector: "Connector",
    apps: "Apps",
    home: "Start",
    analytics: "Statistieken",
    tools: "Tools",
    docs: "Handleiding",
    settings: "Instellingen",
    personal: "Persoonlijk",
    organization: "Organisatie",
    developer: "Ontwikkelaars",
    general: "Algemeen",
    account: "Account",
    people: "Mensen en teams",
    roadmap: "Roadmap",
    activity: "Logboek",
    about: "Over",
    viewAs: "Bekijken als",
  },
  shell: {
    skipToContent: "Naar de inhoud",
    openMenu: "Menu openen",
    menu: "Menu",
    menuDescription: "De pagina's van het dashboard, de handleiding en je account.",
    homeLink: "{{app}}, naar de startpagina",
    builtBy: "Gemaakt door",
    veltaNewTab: "Velta (opent in een nieuw tabblad)",
    collapseMenu: "Menu inklappen",
    expandMenu: "Menu uitklappen",
  },
  environment: {
    readOnly: "Alleen lezen",
    title: "Omgeving {{name}}, los van de productie.",
    titleReadOnly:
      "Omgeving {{name}}, los van de productie: ze leest de echte gegevens van De Ceuster, maar wijzigt nooit iets in Odoo of een ander systeem.",
  },
  accountMenu: {
    fallbackName: "Account",
    signOut: "Afmelden",
    persona: "Persona (lokaal)",
    personaNote: "Alleen in een lokale testomgeving",
  },
  session: {
    reconnecting: "Opnieuw verbinden…",
    serverDown: "De server antwoordt niet. Deze pagina gaat vanzelf verder zodra hij terug is.",
  },
  errorPage: {
    title: "Er ging iets mis",
    body: "Laad de pagina opnieuw om het nog eens te proberen.",
    reload: "Opnieuw laden",
  },
  notFound: {
    title: "Pagina niet gevonden",
    home: "Naar de startpagina",
  },
  connector: {
    microsoftDetail: "Aanmelden met je Microsoft-account van De Ceuster",
    localLabel: "Lokale test",
    localDetail: "Zonder aanmelding, alleen op deze computer",
    offDetail: "De connector staat nog niet aan",
    odooOnDetail: "Claude leest in de Odoo van De Ceuster, zonder iets te wijzigen",
    odooConnectorOffDetail: "Zolang de connector uit staat",
    odooNotConnectedDetail: "Tot Odoo gekoppeld is",
  },
  usage: {
    lastDays_one: "Vandaag",
    lastDays_other: "Afgelopen {{count}} dagen",
    noCalls: "Geen oproepen van de connector in de afgelopen {{count}} dagen.",
    calls: "Oproepen",
    activeUsers: "Actieve gebruikers",
    errors: "Fouten",
  },
  chart: {
    successful: "Geslaagd",
    errors: "Fouten",
    calls_one: "{{count, number}} oproep",
    calls_other: "{{count, number}} oproepen",
    errorCount_one: "{{count, number}} fout",
    errorCount_other: "{{count, number}} fouten",
    summary: "Oproepen per dag over {{days}} dagen: {{calls}}, {{errors}}.",
    dayDescription: "{{day}}: {{calls}}, {{errors}}",
    noCallsDay: "{{day}}: geen oproepen",
    errorDayDescription: "{{day}}: {{errors}} van {{calls}} ({{share}})",
    measure: {
      label: "Weergave",
      count: "Aantal",
      percent: "Percentage",
    },
  },
  table: {
    sortedAscending: "oplopend gesorteerd",
    sortedDescending: "aflopend gesorteerd",
  },
  filters: {
    search: "Zoeken",
    empty: "Niets gevonden.",
    clear: "Wissen",
    toolCount_one: "{{count, number}} tool",
    toolCount_other: "{{count, number}} tools",
    personCount_one: "{{count, number}} persoon",
    personCount_other: "{{count, number}} personen",
    actionCount_one: "{{count, number}} actie",
    actionCount_other: "{{count, number}} acties",
  },
  period: {
    label: "Periode",
    last: {
      week: "Afgelopen week",
      month: "Afgelopen maand",
      quarter: "Afgelopen kwartaal",
    },
  },
  timeRange: {
    label: "Tijdvak",
    value: "Tijdvak: {{range}}",
    custom: "Aangepast",
    range: "{{from}} tot {{to}}",
    from: "Van",
    to: "Tot",
    startDay: "Begindag",
    endDay: "Einddag",
    startTime: "Beginuur",
    endTime: "Einduur",
    chooseDay: "Kies een dag",
    apply: "Toepassen",
    belgianTime: "In Belgische tijd. Het einde telt tot en met die minuut.",
    problem: {
      day: "Kies een dag voor het begin en voor het einde.",
      time: "Typ een uur als 08:30, tussen 00:00 en 23:59.",
      order: "Het einde moet na het begin liggen.",
      tooLong: "Kies een tijdvak van hoogstens een jaar.",
    },
  },
  dayPicker: {
    value: "{{label}}: {{day}}",
    previousMonth: "Vorige maand",
    nextMonth: "Volgende maand",
    clear: "Wissen",
  },
  home: {
    morning: "Goedemorgen",
    afternoon: "Goedenamiddag",
    evening: "Goedenavond",
    status: "Status",
    odooTools: "Odoo-tools",
    statusLoadError: "De status van de connector kon niet geladen worden.",
    usageLoadError: "Het gebruik van de connector kon niet geladen worden.",
    docsTitle: "Zo gebruik je de connector in Claude",
    docsBody:
      "Koppel Claude één keer met je Microsoft-account van De Ceuster en stel daarna gewoon je vragen over klanten, kranen, orders en facturen. De handleiding legt uit hoe, wat je kunt vragen en wat de connector bijhoudt.",
    docsLink: "Lees de handleiding",
    issues: {
      title: "Fouten om te analyseren",
      failedLastWeek_one: "{{count, number}} keer mislukt in de afgelopen 7 dagen",
      failedLastWeek_other: "{{count, number}} keer mislukt in de afgelopen 7 dagen",
      lastFailed: "laatst mislukt op {{time}}",
      more_one: "Nog {{count, number}} probleem om te analyseren",
      more_other: "Nog {{count, number}} problemen om te analyseren",
      acknowledged_one: "{{count, number}} in behandeling bij Velta",
      acknowledged_other: "{{count, number}} in behandeling bij Velta",
    },
  },
  tools: {
    retired: {
      label: "Niet meer beschikbaar",
      hint: "DC Data biedt deze tool niet meer aan. Zijn eerdere oproepen blijven bewaard en zichtbaar.",
    },
    description: "De tools die Claude via DC Data voor je kan gebruiken.",
    loadError: "De tools konden niet geladen worden.",
    switchesUnknown:
      "De schakelaars van de tools konden net niet gelezen worden. Probeer het over een minuut opnieuw.",
    access: {
      read: "Lezen",
      write: "Schrijven",
    },
    accessLetter: {
      read: "L",
      write: "S",
    },
    comingSoon: "Binnenkort",
    paused: "Tijdelijk uit",
    personalSwitchLabel: "Claude {{title}} voor mij laten gebruiken",
    alwaysOn: "Altijd aan",
    unavailable: "Niet beschikbaar",
    unavailableReason: {
      connectorOff: "De connector staat uit",
      needsSignIn: "Alleen met Microsoft-aanmelding",
      writesOff: "Alleen lezen in deze omgeving",
      odooNotConnected: "Odoo is niet gekoppeld",
      appOff: "De app {{app}} staat uit",
    },
    column: {
      tool: "Tool",
      audience: "Voor",
      personal: "Voor mij",
    },
    audience: {
      everyone: "Iedereen",
      team: "Per team",
      admin: "Beheerders",
      owner: "Eigenaar",
      developer: "Ontwikkelaars",
    },
    audienceShort: {
      developer: "Ontw.",
    },
    audienceHint: {
      everyone: "Elke collega",
      team: "Wie in een team zit dat deze groep krijgt, zie Mensen en teams",
      admin: "Beheerders, de eigenaar en de ontwikkelaars",
      owner: "De eigenaar en de ontwikkelaars",
      developer: "Alleen ontwikkelaars",
    },
    groupCount: {
      tools_one: "{{count, number}} tool",
      tools_other: "{{count, number}} tools",
      read: "{{count, number}} lezen",
      write: "{{count, number}} schrijven",
      soon: "{{count, number}} binnenkort",
    },
    kind: {
      odoo: "Odoo",
      account: "Je account",
      submission: "Ideeën en vragen",
      role: "Voor je rol",
    },
    filter: {
      search: "Zoek op naam of omschrijving",
      allAccess: "Alle toegang",
      kind: "Soort",
      allKinds: "Alle soorten",
      group: "Groep",
      allGroups: "Alle groepen",
      groupCount_one: "{{count}} groep",
      groupCount_other: "{{count}} groepen",
      empty: "Geen tools gevonden.",
    },
    details: {
      access: "Toegang",
      group: "Groep",
      audience: "Voor",
      size: "Grootte",
      sizeValue: "ongeveer {{tokens}} tokens",
      usage: "Gebruik",
      usageLink: "Oproepen op Statistieken",
      comingSoon: "Claude kan deze tool nog niet gebruiken.",
      notServed: "Claude krijgt deze tool hier niet.",
      notServedBecause: "Claude krijgt deze tool hier niet. {{reason}}.",
      claudeReads: "Wat Claude leest, in het Engels",
      noDescription: "Claude krijgt geen beschrijving bij deze tool.",
      parameters: "Parameters",
      noParameters: "Deze tool heeft geen parameters.",
      parameter: {
        name: "Parameter",
        type: "Type",
        required: "Verplicht",
        default: "Standaard",
      },
      yes: "Ja",
      no: "Nee",
      requiredFact: "verplicht",
      defaultFact: "standaard {{value}}",
      loading: "De beschrijving voor Claude wordt geladen…",
      killSwitch: "Uit voor iedereen",
      killSwitchHint: "Zolang dit aanstaat, krijgt niemand deze tool in Claude.",
    },
  },
  analytics: {
    description:
      "Gebruik van de MCP-connector DC Data in Claude: alleen metadata, nooit wat er gevraagd of geantwoord werd.",
    tabs: {
      overview: "Overzicht",
      calls: "Oproepen",
      failures: "Fouten",
      speed: "Snelheid",
    },
    loadError: "Het gebruik van de connector kon niet geladen worden.",
    stale:
      "De gegevens van de connector konden niet vernieuwd worden, dus je ziet wat het laatst geladen werd.",
    failedCalls: "Mislukte oproepen",
    shareOfCalls: "{{share}} van de oproepen",
    callsPerDay: "Oproepen per dag",
    byUser: "Per persoon",
    byTool: "Per tool",
    tables: "Tabellen",
    usersEmpty: "Niemand gebruikte de connector in deze periode.",
    usersSearch: "Zoek op naam of e-mail",
    usersNoMatch: "Niemand gevonden.",
    user: "Gebruiker",
    person: "Persoon",
    people: "Personen",
    everyone: "Iedereen",
    searchPerson: "Zoek een persoon",
    errors: "Fouten",
    lastCall: "Laatste oproep",
    lastCallFact: "laatste oproep {{time}}",
    status: "Status",
    onlyWithErrors: "Alleen met fouten",
    blocked: "Geblokkeerd",
    blockedHint:
      "Velta heeft deze persoon de toegang tot DC Data afgenomen. Blokkeren en deblokkeren doe je bij Mensen en teams, op het tabblad Teams.",
    withoutSignIn: "Zonder aanmelding",
    allTools: "Alle tools",
    searchTool: "Zoek een tool",
    outcome: {
      label: "Resultaat",
      all: "Alle resultaten",
      ok: "Geslaagd",
      failed: "Mislukt",
    },
    time: "Tijdstip",
    tool: "Tool",
    duration: "Duur",
    callsLoadError: "De oproepen konden niet geladen worden.",
    callsEmpty: "Geen oproepen gevonden.",
    ok: "OK",
    error: "Fout",
    previousPage: "Vorige",
    nextPage: "Volgende",
    toolsEmpty: "Geen oproepen van tools in deze periode.",
    calls: "Oproepen",
    callsColumn: "Naar de oproepen",
    callsOf: "Oproepen van {{name}} bekijken",
    meanDuration: "Gemiddelde duur",
    meanFact: "gemiddeld {{duration}}",
    failuresFact: "{{failures}} mislukt",
    detail: {
      column: "Details",
      toggle: "Details van de oproep van {{time}}",
      failure: "Mislukt:",
      toIssue: "Naar het probleem",
      noOdoo:
        "Deze oproep ging niet naar Odoo, of is ouder dan 1 november 2026, toen DC Data de cijfers van Odoo begon bij te houden.",
      figures: {
        odoo_requests: "Verzoeken aan Odoo",
        odoo_ms: "Tijd in Odoo",
        odoo_max_ms: "Traagste verzoek",
        worker_wait_ms: "Wachten op een werker",
        spacing_wait_ms: "Wachten op de beurt",
        retry_wait_ms: "Wachten op een nieuwe poging",
        retries: "Nieuwe pogingen",
        rate_limits: "Keer afgeremd door Odoo",
        odoo_status: "Laatste antwoord van Odoo",
      },
    },
  },
  issues: {
    loadError: "De fouten van de connector konden niet geladen worden.",
    perDay: "Mislukte oproepen per dag",
    noFailures: "Geen mislukte oproepen in de afgelopen {{count}} dagen.",
    wholeConnector: "Hele connector",
    roadmapRule: "Niet toegestaan door de regels van de roadmap",
    status: {
      new: {
        label: "Nieuw",
        hint: "Nog niet bekeken door Velta.",
      },
      regressed: {
        label: "Teruggekomen",
        hint: "Velta had dit opgelost, maar het is sindsdien opnieuw mislukt.",
      },
      open: {
        label: "Open",
        hint: "Opnieuw geopend. Velta moet het nog analyseren.",
      },
      acknowledged: {
        label: "In behandeling",
        hint: "Velta analyseert dit probleem. Het blijft in behandeling, ook als het opnieuw mislukt.",
      },
      resolved: {
        label: "Opgelost",
        hint: "Velta heeft dit opgelost. Mislukt het opnieuw, dan komt het terug als Teruggekomen.",
      },
      ignored: {
        label: "Genegeerd",
        hint: "Velta laat dit bewust zo, bijvoorbeeld omdat de connector terecht weigerde.",
      },
    },
    category: {
      refused: "Geweigerd door DC Data",
      request: "Vraag niet te beantwoorden",
      odoo: "Fout van Odoo",
      timeout: "Time-out",
      cancelled: "Afgebroken",
      internal: "Interne fout",
    },
    kinds: {
      user_blocked: "Persoon geblokkeerd",
      user_unidentified: "Persoon niet herkend",
      account_not_allowed: "Geen account van De Ceuster",
      tool_switched_off: "Tool staat uit",
      tool_switched_off_by_user: "Tool zelf uitgezet",
      tool_not_in_team: "Tool niet in het team",
      tool_not_for_role: "Tool niet voor deze rol",
      not_allowed: "Niet toegestaan",
      roadmap_forbidden: "Niet toegestaan voor deze rol",
      tool_not_found: "Onbekende tool",
      unknown_tool: "Onbekende tool",
      invalid_arguments: "Ongeldige vraag",
      not_found: "Niet gevonden",
      refused: "Geweigerd door de tool",
      result_too_large: "Antwoord te groot",
      too_many_rows: "Te veel resultaten",
      triage_entry_not_found: "Niet gevonden in de triage",
      triage_entry_closed: "Al gesloten in de triage",
      odoo_unavailable: "Odoo onbereikbaar",
      odoo_error: "Fout in Odoo",
      access_denied: "Geweigerd door Odoo",
      odoo_access_refused: "Odoo weigert de toegang",
      odoo_data_missing: "Ontbreekt in Odoo",
      odoo_field_missing: "Veld ontbreekt in Odoo",
      odoo_slow: "Odoo te traag",
      odoo_busy: "Odoo bezet",
      odoo_time_budget: "Tijdslimiet bereikt",
      cancelled: "Afgebroken door Claude",
      compute_failed: "Berekening mislukt",
      unavailable: "Niet beschikbaar",
      access_check_unavailable: "Toegang kon niet gecontroleerd worden",
      tool_switches_unavailable: "Schakelaars niet leesbaar",
      team_access_unavailable: "Teams niet leesbaar",
      role_unavailable: "Rol niet leesbaar",
      ErrorResult: "Fout in het antwoord van de tool",
    },
    list: {
      title: "Problemen",
      label: "Problemen",
      search: "Zoek op probleem of tool",
      category: "Soort",
      allStatuses: "Alle statussen",
      allCategories: "Alle soorten",
      noMatch: "Geen problemen gevonden.",
      lastSeenFact: "laatst gezien {{time}}",
      usersFact_one: "{{count, number}} persoon",
      usersFact_other: "{{count, number}} personen",
    },
    columns: {
      problem: "Probleem",
      status: "Status",
      count: "Keer",
      users: "Personen",
      lastSeen: "Laatst gezien",
      firstSeen: "Eerst gezien",
      perDay: "Per dag",
    },
    page: {
      back: "Alle fouten",
      loadError: "Dit probleem kon niet geladen worden.",
      notFound:
        "Dit probleem bestaat niet, of zijn mislukte oproepen zijn niet meer bewaard. DC Data bewaart ze 12 maanden.",
      statusSince: "status sinds {{time}}",
      regressedSince: "opgelost op {{time}} en daarna opnieuw mislukt",
      calls: "Mislukte oproepen",
      noFailures: "Geen mislukte oproepen in de afgelopen {{count}} dagen.",
      facts: {
        inPeriod: "Mislukt in deze periode",
        shareOfToolCalls: "{{share}} van de oproepen van deze tool",
        total: "Mislukt in 12 maanden",
        users: "Personen in deze periode",
        tools: "Tools in deze periode",
        lastSeen: "Laatst gezien",
        firstSeen: "Eerst gezien",
      },
    },
    actions: {
      label: "Acties",
      acknowledge: "In behandeling nemen",
      resolve: "Oplossen",
      ignore: "Negeren",
      reopen: "Heropenen",
      release: "Terug naar open",
      note: "Notitie toevoegen",
    },
    dialog: {
      acknowledge:
        "Het probleem krijgt de status In behandeling. Zo zien de beheerders dat Velta eraan werkt.",
      resolve:
        "Het probleem krijgt de status Opgelost. Mislukt het daarna opnieuw, dan komt het terug als Teruggekomen.",
      ignore:
        "Het probleem krijgt de status Genegeerd en verdwijnt van de startpagina, ook als het opnieuw mislukt.",
      reopen: "Het probleem krijgt opnieuw de status Open en staat weer op de startpagina.",
      release:
        "Het probleem is niet meer in behandeling en krijgt opnieuw de status Open. Zo staat het weer op de startpagina.",
      note: "De notitie komt in de geschiedenis van het probleem. De status blijft zoals hij is.",
      noteLabel: "Notitie",
      noteOptional: "Notitie (niet verplicht)",
      noteHint: "Beheerders, eigenaars en ontwikkelaars zien de notitie, met je naam erbij.",
      noteRequired: "Typ een notitie.",
      noteTooLong: "Een notitie telt hoogstens {{max}} tekens.",
    },
    history: {
      title: "Geschiedenis",
      empty: "Nog niemand deed iets met dit probleem.",
      byAt: "door {{who}} op {{time}}",
      done: {
        acknowledge: "In behandeling genomen",
        resolve: "Opgelost",
        ignore: "Genegeerd",
        reopen: "Heropend",
        note: "Notitie",
      },
    },
  },
  speed: {
    loadError: "De snelheid van de connector kon niet geladen worden.",
    perDay: "Per dag",
    perTool: "Per tool",
    onlyRateLimited: "Alleen afgeremd",
    odooCalls: "Odoo-oproepen",
    noOdooCalls: "Geen oproepen naar Odoo in de afgelopen {{count}} dagen.",
    total: "Totaal",
    tooltipRow: "{{label}}: {{value}}",
    charts: {
      waitShare: {
        title: "Aandeel wachttijd",
        about:
          "Het deel van de tijd van de Odoo-oproepen dat ze per dag wachtten op een vrije werker, op hun beurt of op een nieuwe poging.",
      },
      waitP95: {
        title: "Wachttijd per oproep",
        about:
          "De wachttijd waarbinnen per dag 95% van de Odoo-oproepen aan de beurt kwam (de p95).",
      },
      requestP95: {
        title: "Traagste verzoek aan Odoo",
        about:
          "De tijd waarbinnen Odoo per dag bij 95% van de oproepen ook zijn traagste verzoek beantwoordde (de p95).",
      },
      rateLimited: {
        title: "Afgeremd door Odoo",
        about: "Hoeveel oproepen Odoo per dag afremde omdat er te veel tegelijk kwamen.",
      },
    },
    series: {
      worker: "Wachten op een werker",
      spacing: "Wachten op de beurt",
      retry: "Nieuwe poging",
      wait: "Wachttijd (p95)",
      request: "Traagste verzoek (p95)",
      rateLimited: "Afgeremd",
    },
    columns: {
      odooCalls: "Odoo-oproepen",
      median: "Mediaan",
      p95: "p95",
      wait: "Wachten (p95)",
      request: "Verzoek (p95)",
      rateLimited: "Afgeremd",
    },
    hints: {
      p95: "De duur waarbinnen 95% van de oproepen van de tool klaar was.",
      wait: "De wachttijd waarbinnen 95% van de Odoo-oproepen van de tool aan de beurt kwam.",
      request:
        "De tijd waarbinnen Odoo bij 95% van de oproepen van de tool ook zijn traagste verzoek beantwoordde.",
      rateLimited: "Hoeveel oproepen van de tool Odoo afremde omdat er te veel tegelijk kwamen.",
    },
    facts: {
      median: "mediaan {{duration}}",
      wait: "wachttijd {{duration}}",
      rateLimited_one: "{{count, number}} keer afgeremd",
      rateLimited_other: "{{count, number}} keer afgeremd",
    },
  },
  settings: {
    accountLoadError: "Je instellingen konden niet geladen worden.",
    roleViewNoAccount:
      "Je bekijkt het dashboard als een rol. Een rol heeft geen eigen account, dus er is geen naam of activiteit om te tonen.",
    accountInfo: "Accountgegevens",
    email: "E-mail",
    role: "Rol",
    memberSince: "Lid sinds",
    yourActivity: "Je activiteit op het dashboard",
    noActivity: "Nog geen activiteit.",
    nameTitle: "Naam wijzigen",
    firstName: "Voornaam",
    lastName: "Achternaam",
    nameSaved: "Je naam is opgeslagen.",
    aboutDescription:
      "DC Data, de connector waarmee Claude de Odoo van De Ceuster kan lezen, met dit dashboard om hem te beheren.",
    version: "Versie",
    readMore: "Meer lezen",
    connectClaude: "Claude verbinden",
    privacy: "Privacy en gegevens",
    roleUser: "Lid",
    roleAdmin: "Beheerder",
    roleOwner: "Eigenaar",
    roleDeveloper: "Ontwikkelaar",
  },
  viewAs: {
    description:
      "Bekijk het dashboard zoals iemand anders het ziet, met diens rol, teams en toegang. Zolang je meekijkt, kun je niets wijzigen. Elk begin en einde komt in het logboek.",
    choice: "Wie je bekijkt",
    person: "Een persoon",
    role: "Een rol",
    people: "Mensen",
    search: "Zoek op naam of e-mail",
    allRoles: "Alle rollen",
    roleCount_one: "{{count, number}} rol",
    roleCount_other: "{{count, number}} rollen",
    searchRole: "Zoek een rol",
    column: {
      name: "Naam",
      role: "Rol",
      teams: "Teams",
      lastActive: "Laatst actief",
      actions: "Acties",
    },
    noCalls: "Nog geen oproep",
    lastActiveFact: "Laatst actief op {{time}}",
    action: "Bekijken als",
    actionFor: "Bekijken als {{name}}",
    empty: "Er heeft nog niemand anders een account op het dashboard.",
    noMatch: "Niemand gevonden met deze filters.",
    loadError: "De mensen konden niet geladen worden.",
    roles: "Rollen",
    roleAccess: {
      user: "Een nieuwe collega in geen enkel team: alleen wat iedereen ziet",
      admin: "Elke beheerpagina, en elke tool en app via Beheerders",
      owner: "Een beheerder die ook de uren en dagen op de roadmap ziet",
    },
    bar: {
      label: "Bekijken als",
      person: "Je bekijkt het dashboard als <strong>{{name}}</strong> ({{role}})",
      personShort: "Als <strong>{{name}}</strong> ({{role}})",
      role: {
        user: "Je bekijkt het dashboard als een <strong>{{role}}</strong> zonder team",
        admin: "Je bekijkt het dashboard als een <strong>{{role}}</strong>",
        owner: "Je bekijkt het dashboard als een <strong>{{role}}</strong>",
      },
      roleShort: {
        user: "Als <strong>{{role}}</strong> zonder team",
        admin: "Als <strong>{{role}}</strong>",
        owner: "Als <strong>{{role}}</strong>",
      },
      switch: "Wissel",
      readOnly: "Alleen lezen",
      stop: "Stoppen",
      searchPerson: "Zoek een persoon",
      noPeople: "Niemand gevonden.",
    },
    toast: {
      ended: "De weergave als iemand anders is gestopt.",
      changed: "De weergave is in een ander tabblad gewijzigd.",
    },
  },
  people: {
    tabs: {
      users: "Mensen",
      teams: "Teams",
    },
  },
  users: {
    loadError: "De gebruikers konden niet geladen worden.",
    search: "Zoek op naam of e-mail",
    allRoles: "Alle rollen",
    firstSignIn:
      "Collega's krijgen een account bij hun eerste aanmelding met Microsoft. Een beheerder kan daarna hun rol wijzigen.",
    empty: "Geen gebruikers gevonden.",
    user: "Gebruiker",
    name: "Naam",
    joined: "Lid sinds",
    actions: "Acties",
    view: "Details van {{email}} bekijken",
    viewShort: "Details bekijken",
    edit: "{{email}} bewerken",
    signOutEverywhereFor: "{{email}} overal afmelden",
    deleteFor: "{{email}} verwijderen",
    deleteTitle: "Gebruiker verwijderen",
    deleteBody:
      "Wil je <strong>{{email}}</strong> echt verwijderen? Dit kan niet ongedaan gemaakt worden.",
    detailTitle: "Gebruikersgegevens",
    detailLoadError: "De gegevens van de gebruiker konden niet geladen worden.",
    updated: "Laatst gewijzigd",
    lastSignIn: "Laatst aangemeld",
    recentActivity: "Recente activiteit",
    editTitle: "Gebruiker bewerken",
    signOutEverywhere: "Overal afmelden",
    revokeSelf:
      "Je kunt jezelf hier niet overal afmelden. Gebruik Afmelden, of vraag een andere beheerder om je overal af te melden.",
    roleLockedSelf: "Je kunt je eigen rol niet wijzigen.",
    rolesBecauseOwner: "Omdat je Eigenaar bent",
    rolesBecauseDeveloper: "Omdat je Ontwikkelaar bent",
    lockedOwner:
      "Alleen een eigenaar of een ontwikkelaar kan het account van een eigenaar wijzigen of verwijderen.",
    lockedDeveloper:
      "Alleen een ontwikkelaar kan het account van een ontwikkelaar wijzigen of verwijderen.",
    revokeBody:
      "Alle actieve sessies van <strong>{{email}}</strong> beëindigen? Deze persoon moet zich daarna op elk toestel opnieuw aanmelden.",
  },
  teams: {
    explainTeams:
      "Teams tellen op: wie in meer teams zit, krijgt hun toegang samen. Wie in geen team zit, krijgt de toegang van Iedereen zonder team.",
    explainExceptions:
      "Een uitzondering voor één persoon wint altijd van de teams. Een tool die een ontwikkelaar tijdelijk uitzet, staat voor iedereen uit.",
    loadError: "De teams konden niet geladen worden.",
    newTeam: "Nieuw team",
    defaultTeam: "Iedereen zonder team",
    adminsTeam: "Beheerders",
    adminsTeamDetail: "Beheerders, eigenaars en ontwikkelaars, met alle toegang",
    allAccess: "Alle toegang",
    byRole: "via rol",
    byRoleAndExtra: "via rol + extra",
    column: {
      team: "Team",
      members: "Leden",
      access: "Toegang",
      actions: "Acties",
    },
    filter: {
      search: "Zoek op naam of omschrijving",
      allAccess: "Alle groepen en apps",
      noMatch: "Geen team gevonden.",
    },
    peopleWithoutTeam_one: "{{count, number}} persoon zonder team",
    peopleWithoutTeam_other: "{{count, number}} mensen zonder team",
    memberCount_one: "{{count, number}} lid",
    memberCount_other: "{{count, number}} leden",
    noMembers: "Nog geen leden",
    moreMembers: "+{{count, number}}",
    noAccess: "Geen toegang",
    partTitle: "{{group}}: {{access}}",
    edit: "{{team}} bewerken",
    deleteFor: "{{team}} verwijderen",
    deleteTitle: "Team verwijderen",
    deleteBody:
      "Wil je het team <strong>{{team}}</strong> echt verwijderen? De leden houden de toegang van hun andere teams, of krijgen die van Iedereen zonder team.",
    editor: {
      newTitle: "Nieuw team",
      editTitle: "Team bewerken",
      defaultDescription: "Dit team heeft geen leden.",
      name: "Naam van het team",
      description: "Omschrijving (niet verplicht)",
      access: "Toegang",
      members: "Leden",
      membersHint: "Leden herkennen we aan hun Microsoft-aanmelding (e-mailadres).",
      comingSoon: "binnenkort",
      removeMember: "{{person}} uit het team halen",
      removeExtra: "{{person}} als extra lid weghalen",
      noMembers: "Nog niemand in dit team.",
      adminsDescription:
        "Het team heeft alle toegang, ook tot tools en apps die later bijkomen. Een uitzondering voor één persoon wint nog altijd. Dit vaste team kan niet hernoemd of verwijderd worden.",
      allAccessDetail:
        "Alle groepen om te lezen en te schrijven, alle apps en de tools die alleen voor beheerders zijn.",
      roleMembers: "Beheerders, eigenaars en ontwikkelaars van het dashboard",
      roleMembersHint: "Zij zitten in het team door hun rol. Die wijzig je op het tabblad Mensen.",
      extraMembers: "Extra leden",
      extraMembersHint:
        "Bijvoorbeeld collega's die Claude gebruiken zonder account op het dashboard.",
      addPeople: "Mensen toevoegen",
    },
    people: {
      title: "Mensen en uitzonderingen",
      search: "Zoek op naam of e-mail",
      addException: "Uitzondering toevoegen",
      column: {
        person: "Persoon",
        teams: "Teams",
        access: "Toegang",
        lastCall: "Laatste oproep",
      },
      allTeams: "Alle teams",
      teamCount_one: "{{count, number}} team",
      teamCount_other: "{{count, number}} teams",
      searchTeam: "Zoek een team",
      blocked: "Geblokkeerd",
      withExceptions: "Met uitzonderingen",
      lastCallFact: "Laatste oproep {{time}}",
      noCalls: "Nog geen oproep",
      block: "Blokkeren",
      unblock: "Deblokkeren",
      blockFor: "{{person}} blokkeren",
      unblockFor: "{{person}} deblokkeren",
      neverSignedIn: "Nog nooit aangemeld in Claude",
      blockedAtBy: "Geblokkeerd op {{time}} door {{email}}.",
      blockedAt: "Geblokkeerd op {{time}}.",
      blockedEffect:
        "DC Data weigert elke oproep van deze persoon tot iemand de persoon deblokkeert.",
      empty:
        "Nog niemand gekend. Mensen verschijnen hier zodra ze in een team zitten of de connector gebruikt hebben.",
      noMatch: "Niemand gevonden.",
      exceptions: "Uitzonderingen",
      exceptionsFor: "Uitzonderingen voor {{person}}",
    },
    block: {
      title: "Toegang tot DC Data blokkeren",
      unblockTitle: "Toegang tot DC Data deblokkeren",
      body: "<strong>{{who}}</strong> blokkeren voor DC Data? Binnen een minuut stopt Velta het gebruik van de connector voor deze persoon. Claude krijgt dan geen tools meer, en elke oproep wordt geweigerd. Het Microsoft-account zelf, dat de IT-partner van De Ceuster beheert, blijft zoals het is.",
      unblockBody:
        "Mag <strong>{{who}}</strong> DC Data weer gebruiken? Dat geldt binnen een minuut, zolang deze persoon nog een actief Microsoft-account van De Ceuster heeft.",
      block: "Blokkeren",
      unblock: "Deblokkeren",
    },
    summary: {
      read: "{{groups}} lezen",
      write: "{{groups}} schrijven",
      comingSoon: "binnenkort",
      partly: "deels",
      exception: "uitzondering",
      none: "Geen toegang",
    },
    exceptions: {
      description:
        "Toestaan en Weigeren winnen van de teams, en een uitzondering voor één tool wint van die voor de groep.",
      teams: "Teams: {{teams}}",
      choice: {
        team: "Volgens team",
        allow: "Toestaan",
        deny: "Weigeren",
      },
      allowed: "Toegang",
      denied: "Geen toegang",
      partly: "Deels",
      allTools: {
        read: "Alle leestools",
        write: "Alle schrijftools",
      },
      clearAll: "Alles volgens team",
      choiceLabel: "Uitzondering voor {{unit}}",
    },
    apps: {
      title: "Apps",
      requires: "Vereist {{units}}",
      missing: "Dit team mist nog {{units}}.",
      units: {
        read: "{{groups}}, lezen",
        write: "{{groups}}, schrijven",
      },
      groups: {
        account: "Je account",
        clients: "Klanten",
        cranes: "Kranen",
        orders: "Orders",
        finance: "Financieel",
        system: "Systeem",
      },
      grant: "App {{app}}",
      summary: "app {{apps}}",
      exceptionMissing: "Mist {{units}}",
    },
  },
  picker: {
    search: "Zoek op naam of e-mail",
    empty: "Niemand gevonden.",
    addByEmail: "Toevoegen via e-mail",
    invalidEmail: "Typ een volledig e-mailadres om iemand toe te voegen.",
    domainNotAllowed: "Alleen adressen op {{domains}} kunnen toegang krijgen.",
  },
  roadmap: {
    newIdea: "Nieuw idee",
    proposeIdea: "Idee voorstellen",
    loadError: "De roadmap kon niet geladen worden.",
    empty: "Nog niets op de roadmap. Voeg een eerste idee toe.",
    emptyPropose: "Nog niets op de roadmap. Stel een eerste idee voor.",
    status: {
      idea: "Idee",
      planned: "Gepland",
      in_progress: "Bezig",
      done: "Klaar",
    },
    priority: {
      high: "Hoog",
      medium: "Middel",
      low: "Laag",
    },
    priorityLong: {
      high: "Hoge prioriteit",
      medium: "Gemiddelde prioriteit",
      low: "Lage prioriteit",
    },
    facts: {
      deadline: "deadline {{date}}",
    },
    groups: {
      done: { title: "Klaar" },
      in_progress: { title: "Bezig" },
      planned: { title: "Gepland" },
      idea: { title: "Ideeën" },
    },
    itemCount_one: "{{count, number}} item",
    itemCount_other: "{{count, number}} items",
    groupTotal: "{{items}}, {{effort}}",
    column: {
      item: "Item",
      priority: "Prioriteit",
      effort: "Inspanning",
      month: "Maand",
      deadline: "Deadline",
      followers: "Volgers",
    },
    sort: {
      hint: {
        item: "Sorteer op titel van A tot Z. Klik opnieuw voor Z tot A, en een derde keer voor de standaardvolgorde.",
        priority:
          "Sorteer op prioriteit van laag naar hoog. Klik opnieuw voor hoog naar laag, en een derde keer voor de standaardvolgorde.",
        effort:
          "Sorteer op inspanning van klein naar groot. Klik opnieuw voor groot naar klein, en een derde keer voor de standaardvolgorde.",
        month:
          "Sorteer op maand van vroeg naar laat. Klik opnieuw voor laat naar vroeg, en een derde keer voor de standaardvolgorde.",
        deadline:
          "Sorteer op deadline van vroeg naar laat. Klik opnieuw voor laat naar vroeg, en een derde keer voor de standaardvolgorde.",
        followers:
          "Sorteer op aantal volgers van weinig naar veel. Klik opnieuw voor veel naar weinig, en een derde keer voor de standaardvolgorde.",
      },
      by: {
        item: "titel",
        priority: "prioriteit",
        effort: "inspanning",
        month: "maand",
        deadline: "deadline",
        followers: "aantal volgers",
      },
      asc: {
        item: "van A tot Z",
        priority: "van laag naar hoog",
        effort: "van klein naar groot",
        month: "van vroeg naar laat",
        deadline: "van vroeg naar laat",
        followers: "van weinig naar veel",
      },
      desc: {
        item: "van Z tot A",
        priority: "van hoog naar laag",
        effort: "van groot naar klein",
        month: "van laat naar vroeg",
        deadline: "van laat naar vroeg",
        followers: "van veel naar weinig",
      },
      active: "Elke groep is gesorteerd op {{column}}, {{order}}.",
      reset: "Standaardvolgorde",
    },
    filter: {
      search: "Zoek op titel of omschrijving",
      searchDeveloper: "Zoek op titel, omschrijving of Linear-id",
      priority: "Prioriteit",
      allPriorities: "Alle prioriteiten",
      toggles: "Snelle filters",
      blocked: "Geblokkeerd",
      waitingOnMe: "Wacht op mij",
      followedByMe: "Gevolgd door mij",
      empty: "Geen items gevonden.",
    },
    effort: {
      hours: "{{hours}} u",
      day: "{{days}} dag",
      days: "{{days}} dagen",
      unknown: "Nog te schatten",
      estimated: "geschat",
      actual: "werkelijk",
      hoursAndDays: "{{hours}} ({{days}})",
      less: "Minder inspanning",
      more: "Meer inspanning",
    },
    blockers: {
      symbol: "Blokkade",
      separator: ": ",
      itemStatus: "({{status}})",
      more_one: "+{{count, number}} andere blokkade",
      more_other: "+{{count, number}} andere blokkades",
      nothing: "Wacht op niets of niemand",
      since: "sinds {{date}}",
      resolvedBy: "opgelost door {{person}} op {{date}}",
      resolvedOn: "opgelost op {{date}}",
      resolve: "Opgelost",
      resolveLabel: "Blokkade van {{who}} als opgelost markeren",
      remove: "Blokkade van {{who}} verwijderen",
      add: "Blokkade",
      addTitle: "Blokkade toevoegen",
      who: "Wie",
      kinds: {
        people: "Personen",
        item: "Item",
        party: "Anders",
      },
      choosePeople: "Personen kiezen",
      chooseItem: "Item kiezen",
      other: "Waarop het wacht",
      otherPlaceholder: "Bijvoorbeeld een leverancier",
      otherTooLong: "De tekst bij Anders mag hoogstens {{max}} tekens tellen. Maak hem korter.",
      ask: "Wat",
      askCount: "{{length}}/{{max}}",
      askCountLabel: "{{length}} van {{max}} tekens",
      askTooLong: "“Wat” mag hoogstens {{max}} tekens tellen. Maak het korter.",
      askOptional: "Wat (niet verplicht)",
      askHint: "Schrijf het zo dat het na de naam leest, bijvoorbeeld “de prijslijst bezorgen”.",
      create: "Toevoegen",
      card: "Blokkades",
      cardNone: "geen",
      cardOpen_one: "{{count, number}} open",
      cardOpen_other: "{{count, number}} open",
      removeMode: "Blokkades verwijderen",
      removeDone: "Klaar met verwijderen",
      resolvedCount_one: "{{count, number}} opgelost",
      resolvedCount_other: "{{count, number}} opgelost",
    },
    followers: {
      unfollow: "Niet meer volgen",
      followedBy: "Gevolgd door {{names}}",
      follow: "Volgen",
      followItem: "{{item}} volgen",
      more: "+{{count, number}}",
      person: "{{name}}, {{role}}",
      none: "Nog geen volgers",
      closed: "Dit item is intussen afgewerkt, dus de volgers liggen vast.",
    },
    month: {
      budgetHint: "In hele uren, een dag telt 8 uur.",
      budgetInvalid: "Geef een heel aantal uren, 0 of meer.",
      previous: "Vorige maand",
      next: "Volgende maand",
      budget: "Budget",
      done: "Afgewerkt",
      planned: "Nog gepland",
      left: "Over",
      over: "Boven budget",
      editBudget: "Budget wijzigen",
      budgetHours: "Budget van {{month}} in uren",
      legendDone: "Afgewerkt",
      legendPlanned: "Gepland of bezig",
      legendOver: "Boven budget",
      budgetLine: "Budget {{effort}}",
      chart:
        "In {{month}} is {{done}} werk afgewerkt en {{planned}} werk nog gepland, op een budget van {{budget}}.",
      chartProgress: "In {{month}} {{done}} en {{planned}}.",
      chartDone_one: "is {{count, number}} item afgewerkt",
      chartDone_other: "zijn {{count, number}} items afgewerkt",
      chartPlanned_one: "is {{count, number}} item gepland of bezig",
      chartPlanned_other: "zijn {{count, number}} items gepland of bezig",
      segment: "{{title}}, {{hours}}",
      empty: "Nog niets ingepland in {{month}}.",
      emptyPast: "Niets afgewerkt in {{month}}.",
      confirmBody: "Het budget van {{month}} gaat van {{from}} naar {{to}}.",
      confirmNotify: "Het ontwikkelteam wordt op de hoogte gebracht.",
      confirm: "Bevestigen",
      budgetForbidden: "Met je rol kun je het budget van deze maand niet wijzigen.",
    },
    details: {
      noSummary: "Geen omschrijving.",
      summary: "Omschrijving",
      editContent: "Titel, omschrijving en prioriteit bewerken",
      editText: "Titel en omschrijving bewerken",
      editPriority: "Prioriteit wijzigen",
      editingContent: "Titel, omschrijving en prioriteit bewerken",
      editingText: "Titel en omschrijving bewerken",
      editingPriority: "Prioriteit wijzigen",
      stage: "Levensloop",
      passed: "(voorbij)",
      ideaSince: "sinds {{date}}",
      planIn: "Inplannen in",
      planOther: "Andere maand",
      effortByVelta: "Velta kiest eerst een inspanning.",
      doneOn: "Afgewerkt op {{date}}",
      waitsStart: "Velta zet het op Bezig zodra het werk start.",
      waitsFinish: "Velta markeert het als klaar zodra het af is.",
      openBlockers_one: "Wacht nog op {{count, number}} blokkade",
      openBlockers_other: "Wacht nog op {{count, number}} blokkades",
      planning: "Planning",
      editPlanning: "Planning bewerken",
      notPlanned: "Nog niet ingepland",
      monthAtPlan: "Kies je bij het inplannen.",
      people: "Personen",
      effort: "Inspanning",
      month: "Maand",
      deadline: "Deadline",
      none: "Geen",
      addedBy: "Toegevoegd door {{person}} op {{date}}",
      lockedInProgress:
        "Dit item is in uitvoering, dus de status, de titel, de omschrijving, de deadline en de maand liggen vast.",
      lockedDone: "Dit item is afgewerkt en ligt vast.",
      lockedPast: "Dit is een voorbije maand, dus hier verander je niets meer aan dit item.",
      unplan: "Terug naar ideeën",
      start: "Start",
      unstart: "Terug naar gepland",
      finish: "Markeer klaar",
      delete: "Verwijderen",
      finishActual: "Werkelijke uren (niet verplicht)",
      finishHint: "Laat je dit leeg, dan telt de schatting.",
      deleteBody:
        "Wil je <strong>{{item}}</strong> echt verwijderen? Dit kan niet ongedaan gemaakt worden.",
      openItem: "{{item}} openen",
    },
    statusMenu: {
      plan: "Inplannen in {{month}}",
      finish: "Markeer klaar…",
      needsEffort: "Eerst een inspanning kiezen",
      trigger: "{{status}}. Status van {{item}} wijzigen",
      tooltip: "{{status}}. Klik om de status te wijzigen.",
    },
    form: {
      noDeadline: "Geen deadline",
      newTitle: "Nieuw idee",
      newDescription:
        "Een nieuw idee komt rechtstreeks bij Ideeën, met de Linear-tickets erachter.",
      proposeDescription: "Velta bekijkt elk voorstel en zet het op de roadmap waar dat past.",
      title: "Titel",
      titleRequired: "Geef het item een titel.",
      summary: "Omschrijving",
      priority: "Prioriteit",
      priorityOptional: "Prioriteit (niet verplicht)",
      noPriority: "Geen voorkeur",
      language: "Taal",
      languageHint: "De taal waarin de titel en de omschrijving geschreven zijn.",
      linear: "Linear-tickets",
      linearPlaceholder: "ABC-12, ABC-13",
      linearHint: "Een of meer, gescheiden door komma's of spaties, zoals ABC-12, ABC-13.",
      linearRequired: "Geef minstens één Linear-id, zoals ABC-12.",
      linearInvalid_one: "{{ids}} is geen Linear-id. Een Linear-id ziet eruit als ABC-12.",
      linearInvalid_other: "{{ids}} zijn geen Linear-ids. Een Linear-id ziet eruit als ABC-12.",
      linearTooMany: "Een item heeft hoogstens {{max}} Linear-tickets.",
      effortOptional: "Inspanning (niet verplicht)",
      propose: "Voorstellen",
      proposed: "Bedankt. Velta bekijkt je idee en zet het op de roadmap waar dat past.",
      itemSearch: "Zoek een item",
      noItems: "Geen items gevonden.",
      remove: "{{name}} weghalen",
      create: "Idee toevoegen",
    },
    developer: {
      noLinear: "Nog geen tickets.",
      estimateRequired: "Een item dat ingepland is, houdt een schatting.",
      actualInvalid: "Werkelijke uren zijn een heel aantal uren, 0 of meer.",
      title: "Ontwikkelaar",
      edit: "Ontwikkelaar bewerken",
      actual: "Werkelijke uren",
      actualMissing: "Niet ingevuld, de schatting telt.",
      actualAtFinish: "Vul je in bij Markeer klaar.",
      hoursUnit: "u",
      linear: "Linear-tickets",
      linearAdd: "Linear-id toevoegen",
      linearInput: "Linear-id, zoals ABC-123",
      linearInvalid: "Een Linear-id ziet eruit als ABC-123.",
      linearRemove: "{{id}} weghalen",
      linearOpen: "{{id}} in Linear (opent in een nieuw tabblad)",
      commandCopy: "Commando kopiëren",
      commandCopied: "Gekopieerd",
      commandCopiedStatus: "Het commando is gekopieerd.",
      commandCopyFailed: "Kopiëren lukte niet. Selecteer het commando en kopieer het zelf.",
    },
    comments: {
      card: "Opmerkingen",
      none: "Nog geen opmerkingen.",
      loadError: "De opmerkingen konden niet geladen worden.",
      input: "Nieuwe opmerking",
      placeholder: "Schrijf een opmerking. Typ @ om iemand te vermelden.",
      post: "Plaats",
      mentionList: "Iemand vermelden",
      delete: "Opmerking van {{person}} verwijderen",
      deleteBody: "Wil je deze opmerking echt verwijderen? Dit kan niet ongedaan gemaakt worden.",
      closed: "Dit item is afgewerkt, dus er komen geen opmerkingen meer bij.",
      count_one: "{{count, number}} opmerking",
      count_other: "{{count, number}} opmerkingen",
    },
    triage: {
      title: "Triage",
      kinds: {
        idea: "Idee",
        question: "Vraag",
      },
      submittedBy: "Ingediend door {{person}} op {{date}}",
      reasons: {
        missing_tool: "Ontbrekende tool",
        missing_rights: "Ontbrekende rechten",
        odoo_data_missing: "Ontbreekt in Odoo",
        access_withheld: "Toegang afgeschermd",
        other: "Andere",
      },
      showDetails: "Details",
      body: {
        idea: "Omschrijving",
        question: "Vraag",
      },
      toolsTried: "Geprobeerde tools",
      noTools: "Geen",
      detail: "Toelichting van Claude",
      actions: {
        idea: "Acties voor het idee van {{person}}",
        question: "Acties voor de vraag van {{person}}",
      },
      accept: "Naar backlog",
      acceptLabel: {
        idea: "Idee van {{person}} naar de backlog",
        question: "Vraag van {{person}} naar de backlog",
      },
      close: "Sluiten",
      closeLabel: {
        idea: "Idee van {{person}} sluiten",
        question: "Vraag van {{person}} sluiten",
      },
      acceptDescription: {
        idea: "Het item komt bij Ideeën en verdwijnt uit Triage.",
        question:
          "Het item komt bij Ideeën en de vraag verdwijnt uit Triage. Schrijf de titel en de omschrijving in je eigen woorden en laat gegevens van klanten weg. De vraag zelf komt niet op de roadmap.",
      },
      closeTitle: {
        idea: "Idee sluiten",
        question: "Vraag sluiten",
      },
      closeDescription: {
        idea: "Het idee verdwijnt uit Triage zonder op de roadmap te komen.",
        question: "De vraag verdwijnt uit Triage zonder op de roadmap te komen.",
      },
      closeNote: "Notitie (niet verplicht)",
      closeNoteHint:
        "Waarom je het sluit, bijvoorbeeld “al beantwoord” of “past niet bij DC Data”. Wie het instuurde, leest deze notitie in Claude.",
      merge: "Samenvoegen met…",
      mergeLabel: {
        idea: "Idee van {{person}} samenvoegen met een item",
        question: "Vraag van {{person}} samenvoegen met een item",
      },
      mergeTitle: {
        idea: "Idee samenvoegen",
        question: "Vraag samenvoegen",
      },
      mergeDescription: {
        idea: "Het idee verdwijnt uit Triage en hoort voortaan bij een item dat al op de roadmap staat. Wie het indiende, volgt dat item als die de roadmap kan zien.",
        question:
          "De vraag verdwijnt uit Triage en hoort voortaan bij een item dat al op de roadmap staat. De vraag zelf komt niet op de roadmap.",
      },
      mergeItem: "Item",
      mergeItemHint: "Alle items die nog niet klaar zijn.",
      mergeChoose: "Item kiezen",
      mergeConfirm: "Samenvoegen",
      mergeItemDone: "Dit item is intussen klaar. Kies een ander item.",
      mergeItemGone: "Dit item staat niet meer op de roadmap. Kies een ander item.",
      delete: "Verwijderen",
      deleteLabel: {
        idea: "Idee van {{person}} verwijderen",
        question: "Vraag van {{person}} verwijderen",
      },
      deleteTitle: {
        idea: "Idee verwijderen",
        question: "Vraag verwijderen",
      },
      deleteDescription: {
        idea: "Het idee wordt definitief gewist, ook de tekst die {{person}} indiende. Dit kan niet ongedaan gemaakt worden. Sluiten haalt het uit Triage zonder het te wissen.",
        question:
          "De vraag wordt definitief gewist, ook de tekst die {{person}} indiende. Dit kan niet ongedaan gemaakt worden. Sluiten haalt ze uit Triage zonder ze te wissen.",
      },
    },
  },
  activity: {
    loadError: "De activiteit kon niet geladen worden.",
    events: "Gebeurtenissen",
    logins: "Aanmeldingen",
    overTime: "Activiteit in de tijd",
    allEvents: "Alle gebeurtenissen",
    byAction: "Per actie",
    recent: "Recente activiteit",
    search: "Activiteit zoeken…",
    people: "Personen",
    allPeople: "Iedereen",
    searchPerson: "Zoek een persoon",
    actionsFilter: "Acties",
    allActions: "Alle acties",
    searchAction: "Zoek een actie",
    previous: "Vorige",
    next: "Volgende",
    page: "Pagina {{page, number}} van {{total, number}}",
    action: "Actie",
    when: "Wanneer",
    empty: "Geen activiteit gevonden.",
    dayTooltip: "{{day}}: {{events}}, {{logins}}",
    eventCount_one: "{{count, number}} gebeurtenis",
    eventCount_other: "{{count, number}} gebeurtenissen",
    loginCount_one: "{{count, number}} aanmelding",
    loginCount_other: "{{count, number}} aanmeldingen",
    actions: {
      login: "Aangemeld",
      signup: "Geregistreerd",
      profile_update: "Naam gewijzigd",
      password_change: "Wachtwoord gewijzigd",
      admin_update_user: "Gebruiker bewerkt",
      admin_delete_user: "Gebruiker verwijderd",
      admin_revoke_sessions: "Overal afgemeld",
      admin_invite: "Gebruiker uitgenodigd",
      admin_first_account: "Eerste account beheerder gemaakt",
      admin_mcp_enable_tool: "Tool aangezet",
      admin_mcp_disable_tool: "Tool uitgezet",
      admin_mcp_enable_tool_group: "Toolgroep aangezet",
      admin_mcp_disable_tool_group: "Toolgroep uitgezet",
      admin_mcp_block_user: "Connectorgebruiker geblokkeerd",
      admin_mcp_unblock_user: "Connectorgebruiker gedeblokkeerd",
      admin_mcp_team_create: "Team aangemaakt",
      admin_mcp_team_update: "Team gewijzigd",
      admin_mcp_team_delete: "Team verwijderd",
      admin_mcp_team_members: "Teamleden gewijzigd",
      admin_mcp_person_override: "Uitzondering gewijzigd",
      admin_mcp_issue_acknowledge: "Probleem in behandeling genomen",
      admin_mcp_issue_resolve: "Probleem opgelost",
      admin_mcp_issue_ignore: "Probleem genegeerd",
      admin_mcp_issue_reopen: "Probleem heropend",
      roadmap_item_create: "Roadmap-item toegevoegd",
      roadmap_item_update: "Roadmap-item gewijzigd",
      roadmap_item_status: "Status van roadmap-item gewijzigd",
      roadmap_item_delete: "Roadmap-item verwijderd",
      roadmap_item_effort: "Inspanning van roadmap-item gewijzigd",
      roadmap_month_budget: "Maandbudget gewijzigd",
      roadmap_item_blocker: "Blokkade van roadmap-item gewijzigd",
      roadmap_item_comment: "Opmerking bij roadmap-item geplaatst of verwijderd",
      roadmap_gap_promote: "Vraag omgezet in roadmap-idee",
      roadmap_gap_close: "Vraag als beantwoord gemarkeerd",
      roadmap_triage_propose: "Roadmap-idee voorgesteld",
      roadmap_triage_accept: "Idee of vraag uit Triage op de roadmap gezet",
      roadmap_triage_close: "Idee of vraag in Triage gesloten of verwijderd",
      view_as_start: "Bekijken als gestart",
      view_as_switch: "Bekijken als gewisseld",
      view_as_stop: "Bekijken als gestopt",
    },
  },
  apps: {
    bezetting: {
      title: "Bezettingsgraad",
      summary:
        "Hoeveel van de vloot vandaag verhuurd is, wat geboekt staat en hoe het evolueert, per kraan, werkgebied en klant.",
    },
    maandrapport: {
      title: "Maandrapport",
      summary:
        "Omzet, klanten, betalingen en prijzen maand per maand, tegenover dezelfde periode vorig jaar.",
      emptyBody:
        "Herbereken leest de gegevens in Odoo en berekent daaruit het maandrapport. Dat duurt enkele minuten, en je kunt intussen gewoon verder werken.",
      outdatedTitle: "Het maandrapport wordt bijgewerkt",
      outdatedBody:
        "Deze gegevens worden opnieuw berekend, want het maandrapport kreeg een nieuwe versie. Dat duurt enkele minuten. Loopt er geen herberekening, kies dan Herbereken.",
      jobErrors: {
        odoo_data_missing:
          "Herberekenen lukte niet, want Odoo gaf geen facturen, orders, betalingen of lijstprijzen terug.",
      },
    },
    unread_one: "{{count, number}} ongelezen melding",
    unread_other: "{{count, number}} ongelezen meldingen",
    linkWithUnread: "{{app}}, {{unread}}",
    loadError: "{{app}} kon niet geladen worden.",
    meldingenError: "De meldingen konden niet geladen worden. Deze pagina toont ze daarom niet.",
    pageError:
      "Een deel van deze pagina kon niet getoond worden. Laad de pagina opnieuw om het nog eens te proberen.",
    previousKept: "Je ziet nog de vorige gegevens.",
    denied: {
      title: "Geen toegang",
      contactsTitle: "Wie toegang geeft",
      noContacts: "Vraag het aan een beheerder van het dashboard.",
    },
    empty: {
      title: "Nog geen gegevens",
      body: "Herbereken leest de gegevens in Odoo en berekent daaruit de bezettingsgraad. Dat duurt enkele minuten, en je kunt intussen gewoon verder werken.",
    },
    recompute: {
      start: "Herbereken",
      running: "Bezig met herberekenen…",
      waiting: "Herberekenen kan weer om {{time}}",
      unavailable: "Herberekenen kan niet zolang Odoo niet gekoppeld is",
      runningPercent: "Bezig met herberekenen, {{percent}}",
    },
    progress: {
      title: "Herbereken",
      lead: "De huidige cijfers blijven zichtbaar tot de nieuwe klaar zijn.",
      leadFirst: "De cijfers verschijnen op deze pagina zodra ze klaar zijn.",
      label: "Voortgang van het herberekenen",
      running: "Al {{time}} bezig",
      finished: "Klaar na {{time}}",
      stopped: "Gestopt na {{time}}",
      done: "Klaar. De pagina toont nu de Odoo-stand van {{time}}.",
      notLoaded: "De nieuwe cijfers zijn berekend, maar ze konden nog niet geladen worden.",
      hide: "Verbergen",
      steps: {
        odoo_read: "Odoo lezen",
        check: "Gegevens controleren",
        build: "Pagina's berekenen",
        store: "Opslaan",
      },
      status: {
        pending: "nog niet begonnen",
        active: "bezig",
        done: "klaar",
        failed: "mislukt",
      },
      read_one: "{{table}}, {{done, number}} van {{count, number}} record",
      read_other: "{{table}}, {{done, number}} van {{count, number}} records",
      readCount_one: "{{table}}, {{count, number}} record",
      readCount_other: "{{table}}, {{count, number}} records",
      tables: {
        sale_order: "Orders",
        sale_order_line: "Orderlijnen",
        account_move: "Facturen",
        account_move_line: "Factuurlijnen",
        res_partner: "Klanten en contacten",
        fleet_vehicle: "Kranen",
        product_product: "Producten",
        res_company: "Bedrijven",
        sale_order_log: "Orderhistoriek",
        account_partial_reconcile: "Afpunten van betalingen",
        product_pricing: "Huurprijzen",
        receivable_line: "Openstaande posten",
        stock_picking: "Leveringen",
        sale_pricing_crane_transport: "Transporttarieven",
        fleet_vehicle_model: "Kraantypes",
        account_analytic_account: "Analytische rekeningen",
      },
      parts: {
        fleet: "Kranen en verhuringen",
        geo: "Werkgebied",
        places: "Werven en plaatsen",
        pages: "Pagina's samenstellen",
        crane_tables: "Kraantabellen",
        finish: "Afwerken en controleren",
        omzet: "Omzet",
        klanten: "Klanten",
        betalingen: "Facturen en betalingen",
        prijzen: "Prijzen",
        ongefactureerd: "Niet gefactureerd werk",
      },
    },
    jobErrors: {
      odoo_not_configured: "Herberekenen lukte niet, want Odoo is niet gekoppeld.",
      odoo_unavailable:
        "Herberekenen lukte niet, want Odoo antwoordde niet. Probeer het over enkele minuten opnieuw.",
      odoo_access_refused:
        "Herberekenen lukte niet, want het Odoo-account mag niet alle nodige gegevens lezen.",
      odoo_field_missing:
        "Herberekenen lukte niet, want een veld in Odoo ontbreekt of is veranderd.",
      odoo_time_budget:
        "Herberekenen lukte niet, want Odoo lezen duurde te lang. Probeer het later opnieuw.",
      odoo_data_missing:
        "Herberekenen lukte niet, want Odoo gaf veel minder kranen dan gewoonlijk of geen verhuringen terug.",
      compute_failed: "Herberekenen lukte niet, want de berekening liep vast.",
      snapshot_invalid:
        "Herberekenen lukte niet, want het resultaat klopte niet. Het werd niet bewaard.",
      store_failed: "Herberekenen lukte niet, want het resultaat kon niet bewaard worden.",
      abandoned: "Herberekenen werd afgebroken omdat het te lang duurde.",
      interrupted: "Herberekenen werd onderbroken omdat de server herstartte.",
      unknown: "Herberekenen lukte niet.",
    },
  },
  login: {
    title: "Aanmelden bij {{app}}",
    microsoft: "Aanmelden met Microsoft",
    hint: "Meld je aan met je De Ceuster Microsoft-account.",
    microsoftErrors: {
      microsoft_cancelled:
        "De aanmelding met Microsoft werd geannuleerd, of je account heeft geen toegang tot deze app.",
      microsoft_failed: "Aanmelden met Microsoft is mislukt. Probeer het opnieuw.",
      email_not_verified:
        "Je Microsoft-account heeft geen bevestigd e-mailadres. Vraag het aan je IT-beheerder.",
      domain_not_allowed: "Met dit e-maildomein kun je je niet aanmelden.",
      no_account: "Er is geen account gevonden voor deze Microsoft-gebruiker.",
    },
  },
  errors: {
    codes: {
      not_authenticated: "Je bent niet aangemeld. Meld je opnieuw aan.",
      invalid_session: "Je sessie is verlopen. Meld je opnieuw aan.",
      session_revoked: "Je sessie is beëindigd. Meld je opnieuw aan.",
      account_not_found: "Er is geen account gevonden. Vraag een beheerder om toegang.",
      admin_required: "Alleen een beheerder kan dit doen.",
      owner_required: "Alleen een eigenaar kan dit doen.",
      developer_required: "Alleen een ontwikkelaar kan dit doen.",
      view_as_read_only:
        "Je bekijkt het dashboard als iemand anders. Zolang je meekijkt, kun je niets wijzigen.",
      view_as_self: "Je kunt het dashboard niet als jezelf bekijken.",
      view_as_ended:
        "Je bekijkt het dashboard niet meer als iemand anders. De pagina laadt opnieuw.",
      auth_unavailable:
        "Je account kon net niet gecontroleerd worden. Probeer het over een minuut opnieuw.",
      revoke_failed: "De sessies konden niet beëindigd worden. Er is niets gewijzigd.",
      user_not_found: "Deze gebruiker bestaat niet meer.",
      invalid_role: "Deze rol bestaat niet.",
      cannot_demote_self: "Je kunt je eigen rol niet wijzigen.",
      cannot_delete_self: "Je kunt je eigen account niet verwijderen.",
      cannot_revoke_self: "Je kunt jezelf hier niet overal afmelden.",
      role_assignment_forbidden: "Met je rol kun je deze rol niet geven.",
      user_change_forbidden: "Met je rol kun je dit account niet wijzigen of verwijderen.",
      tool_not_found: "Deze tool bestaat niet meer.",
      tool_not_switchable: "Deze tool kan niet uitgezet worden.",
      team_not_found: "Dit team bestaat niet meer.",
      team_name_taken: "Er is al een team met deze naam.",
      team_not_deletable: "Iedereen zonder team kan niet verwijderd worden.",
      team_not_editable: "Van Iedereen zonder team kun je alleen de toegang wijzigen.",
      team_member_not_allowed:
        "Alleen e-mailadressen van De Ceuster kunnen lid worden van een team.",
      person_not_allowed: "Uitzonderingen kunnen alleen voor e-mailadressen van De Ceuster.",
      unit_unknown: "Een van deze tools of groepen bestaat niet meer. Laad de pagina opnieuw.",
      service_unavailable: "De dienst is nu niet bereikbaar. Probeer het over een minuut opnieuw.",
      app_access_required: "Je hebt geen toegang tot deze app. Vraag het aan een beheerder.",
      app_not_for_default_team:
        "Iedereen zonder team kan geen app krijgen. Geef de app aan een team.",
      admins_team_not_deletable: "Beheerders is een vast team en kan niet verwijderd worden.",
      admins_team_not_editable: "Van Beheerders kun je alleen de extra leden wijzigen.",
      snapshot_not_found: "Deze gegevens zijn intussen vervangen. Laad de pagina opnieuw.",
      section_not_found: "Dit deel van de gegevens bestaat niet.",
      job_not_found: "Deze herberekening bestaat niet meer.",
      recompute_running: "Er loopt al een herberekening.",
      recompute_too_soon: "Er werd net herberekend. Probeer het over enkele minuten opnieuw.",
      odoo_not_configured: "Odoo is niet gekoppeld, dus herberekenen kan niet.",
      snapshot_outdated:
        "Het maandrapport wordt opnieuw berekend voor de nieuwe versie. Dat duurt enkele minuten.",
      invalid_range: "Deze periode kan niet getoond worden. Kies een andere periode.",
      detail_not_found: "Voor deze keuze zijn er geen gegevens.",
      client_not_found: "Over deze klant zijn er geen gegevens.",
      roadmap_item_not_found: "Dit item staat niet meer op de roadmap.",
      roadmap_forbidden: "Met je rol kun je dit item niet op deze manier wijzigen.",
      roadmap_transition_not_allowed:
        "Dit item kan niet naar die status. Laad de pagina opnieuw om te zien hoe het ervoor staat.",
      roadmap_estimate_required:
        "Dit item heeft eerst een schatting nodig voor het ingepland kan worden.",
      roadmap_waits_loop: "Dit item kan niet wachten op een item dat zelf op dit item wacht.",
      roadmap_unknown_person: "Een van deze mensen heeft geen account op het dashboard.",
      roadmap_linear_id_taken: "Dit Linear-ticket hoort al bij een ander item.",
      roadmap_effort_not_a_step: "Kies een inspanning uit de lijst.",
      roadmap_blocker_not_found: "Deze blokkade staat niet meer bij dit item.",
      roadmap_blocker_invalid: "Kies wie of wat het item tegenhoudt, en zeg wat er moet gebeuren.",
      roadmap_item_done: "Dit item is afgewerkt, dus er komen geen opmerkingen meer bij.",
      roadmap_mention_invalid:
        "Je kunt alleen beheerders, eigenaars en ontwikkelaars van het dashboard vermelden.",
      roadmap_comment_not_found: "Deze opmerking bestaat niet meer.",
      roadmap_triage_not_found: "Dit idee of deze vraag staat niet meer in Triage.",
      roadmap_triage_closed:
        "Dit idee of deze vraag is intussen al op de roadmap gezet of gesloten.",
      tool_issue_not_found:
        "Dit probleem bestaat niet, of zijn mislukte oproepen zijn niet meer bewaard.",
    } satisfies Record<ApiErrorCode, string>,
    fallback: {
      loadToolDefinition: "De beschrijving voor Claude kon niet geladen worden.",
      updateProfile: "Je naam kon niet opgeslagen worden.",
      updateUser: "De gebruiker kon niet bijgewerkt worden.",
      deleteUser: "De gebruiker kon niet verwijderd worden.",
      revokeSessions: "De gebruiker kon niet afgemeld worden.",
      blockUser: "Deze persoon kon niet geblokkeerd worden.",
      unblockUser: "Deze persoon kon niet gedeblokkeerd worden.",
      issueAction: "Dit probleem kon niet bijgewerkt worden.",
      switchToolOn: "Deze tool kon niet aangezet worden. Probeer het over een minuut opnieuw.",
      switchToolOff: "Deze tool kon niet uitgezet worden. Probeer het over een minuut opnieuw.",
      saveTeam: "Het team kon niet opgeslagen worden.",
      deleteTeam: "Het team kon niet verwijderd worden.",
      saveExceptions: "De uitzonderingen konden niet opgeslagen worden.",
      recompute: "Herberekenen kon niet starten. Probeer het over een minuut opnieuw.",
      saveMeldingen: "De melding kon niet opgeslagen worden. Probeer het opnieuw.",
      appData: "De gegevens konden niet geladen worden. Laad de pagina opnieuw.",
      createRoadmapItem: "Het idee kon niet toegevoegd worden.",
      updateRoadmapItem: "Het item kon niet opgeslagen worden.",
      setRoadmapStatus: "De status kon niet gewijzigd worden.",
      setRoadmapEffort: "De inspanning kon niet opgeslagen worden.",
      followRoadmapItem: "Volgen lukte niet. Probeer het opnieuw.",
      deleteRoadmapItem: "Het item kon niet verwijderd worden.",
      setRoadmapBudget: "Het budget kon niet opgeslagen worden.",
      addRoadmapBlocker: "De blokkade kon niet toegevoegd worden.",
      resolveRoadmapBlocker: "De blokkade kon niet als opgelost gemarkeerd worden.",
      deleteRoadmapBlocker: "De blokkade kon niet verwijderd worden.",
      addRoadmapComment: "De opmerking kon niet geplaatst worden.",
      deleteRoadmapComment: "De opmerking kon niet verwijderd worden.",
      proposeRoadmapIdea: "Het idee kon niet voorgesteld worden.",
      acceptRoadmapTriage: "Het item kon niet naar de backlog.",
      closeRoadmapTriage: "Het item kon niet gesloten worden.",
      mergeRoadmapTriage: "Het item kon niet samengevoegd worden.",
      deleteRoadmapTriage: "Het item kon niet verwijderd worden.",
      startViewAs: "Bekijken als kon niet starten. Probeer het opnieuw.",
      stopViewAs: "Stoppen met meekijken lukte niet. Probeer het opnieuw.",
    },
  },
};
